import { BSO, JobPos, TableRange } from './models';

function logToUI(message: string, isError = false) {
    const debugOutput = document.getElementById('debug-output');
    if (!debugOutput) return;

    const messageElement = document.createElement('div');
    messageElement.textContent = `[${new Date().toLocaleTimeString()}] ${message}`;
    
    if (isError) {
        messageElement.style.color = 'red';
    }

    debugOutput.appendChild(messageElement);
    debugOutput.scrollTop = debugOutput.scrollHeight;
}

export class ExcelProcessor {
    private projectItems = [
        "INF6LG0003M", "INF6PR0111M", "INF6PR0112M", "VES6TU0002M", "INF6PR0005M", 
        "INF6PR0007M", "INF6PR0012M", "INF6PR0013M", "INF6PR0014M", "INF6PR0015M",
        "VES6PR0001M", "INF6PR0016M", "INF6PR0017M", "INF6PR0018M", "INF6PR0019M",
        "INF6PR0020M", "INF6PR0021M", "INF6PR0022M", "INF6PR0023M", "INF6PR0024M",
        "INF6PR0025M", "INF6PR0026M", "INF6PR0027M", "INF6SM0299M", "VES6PR0002M",
        "VES6PR0003M", "VES6PR0004M", "RAN6PR0001M", "RAN6PR0002M", "RAN6PR0003M",
        "RAN6PR0004M", "VES6SM0002M", "VES6SM0003M"
    ];

    private logItems = [
        "LOG6SM0001M", "LOG6SM0002M", "LOG6SM0016M", "LOG6SM0017M"
    ];

    public async findTable(): Promise<TableRange> {
    try {
        return await Excel.run(async (context): Promise<TableRange> => {
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            const range = sheet.getUsedRange();
            const values = range.load("values");
            
            await context.sync();
            
            let startRow = -1;
            let endRow = -1;
            
            for (let i = 0; i < values.values.length; i++) {
                const row = values.values[i];
                if (row[0] === "BEGNTABLE") {
                    startRow = i + 1;
                } else if (row[0] === "ENDTABLE") {
                    endRow = i + 1;
                    break;
                }
            }
            
            if (startRow === -1 || endRow === -1) {
                throw new Error("Table markers not found");
            }
            
            return { startRow, endRow };
        });
    } catch (error) {
        console.error("Error finding table:", error);
        throw error;
    }
}

    public async getItemsCount(tableRange: TableRange): Promise<number> {
        try {
            let maxVal = 0;
            
            await Excel.run(async (context) => {
                const sheet = context.workbook.worksheets.getActiveWorksheet();
                const range = sheet.getRangeByIndexes(
                    tableRange.startRow - 1, 1, 
                    tableRange.endRow - tableRange.startRow + 1, 1
                );
                const values = range.load("values");
                
                await context.sync();


                for (const row of values.values) {
                    const val = row[0];
                    if (val != "") {
                        if (typeof val === 'number') {
                            maxVal = Math.max(maxVal, val);
                        } else if (!isNaN(Number(val))) {
                            maxVal = Math.max(maxVal, parseInt(val as string, 10));
                        }
                    }
                }
            });
            logToUI(`Найдено ${maxVal} позиций`);
            return maxVal;
        } catch (error) {
            console.error("Error getting items count:", error);
            throw error;
        }
    }

    public async getItem(index: number, tableRange: TableRange): Promise<JobPos> {
        try {
            let job: JobPos = {
                index: index.toString(),
                name: "",
                razdel: 1,
                price: "",
                count: "",
                bs: { num: "", address: "" }
            };
            
            let currentRazdel = 1;
            let bsNum = "";
            let bsAddress = "";
            
            await Excel.run(async (context) => {
                const sheet = context.workbook.worksheets.getActiveWorksheet();
                const range = sheet.getRangeByIndexes(
                    tableRange.startRow - 1, 1, 
                    tableRange.endRow - tableRange.startRow + 1, 10
                );
                const values = range.load("values");

                await context.sync();
                
                for (let i = 0; i < values.values.length; i++) {
                    const row = values.values[i];
                    const val = row[1]; // Column B (0-based index)
                    
                    if (typeof row[0] === 'number') {
                        
                        const numVal = Number(row[0]);
                        if (numVal === index) {
                            job.name = row[1] as string; // Column C
                            job.razdel = currentRazdel;
                            job.bs.num = bsNum;
                            job.bs.address = bsAddress;
                            job.price = row[5] as string; // Column G
                            job.count = row[6] as string; // Column H
                            break;
                        }
                    } else if (typeof val === 'string') {
                        const strVal = val as string;
                        if (strVal.includes("Раздел")) {
                            currentRazdel = parseInt(strVal.slice(-1), 10);
                        } else if (strVal.includes("БСО")) {
                            bsNum = row[2] as string;
                            bsAddress = row[3] as string;
                        }
                    }
                }
            });
            
            return job;
        } catch (error) {
            console.error("Error getting item:", error);
            throw error;
        }
    }

    public checkProjectItem(strIn: string): boolean {
        return this.projectItems.includes(strIn);
    }

    public checkLSRItem(strIn: string): boolean {
        return strIn === "GSM6LS0001M";
    }

    public checkLOGItem(strIn: string): boolean {
        return this.logItems.includes(strIn);
    }

    public async generateJSON(tableRange: TableRange): Promise<string> {
        const itemsCount = await this.getItemsCount(tableRange);
        const items: any[] = [];
        let bsoNum = "";
        let bsoCount = 1;
        
        // First pass to determine BSO count
        for (let i = 1; i <= itemsCount; i++) {
            const item = await this.getItem(i, tableRange);
            if (item.bs.num) {
                if (bsoNum === "") {
                    bsoNum = item.bs.num;
                } else if (bsoNum !== item.bs.num) {
                    bsoCount = 2;
                }
            }
        }
        
        // Second pass to generate items
        for (let i = 1; i <= itemsCount; i++) {
            const item = await this.getItem(i, tableRange);
            
            let razdel = 4;
            if (bsoCount > 1) {
                if (item.bs.num === bsoNum) {
                    razdel = 6;
                }
            }
            
            if (this.checkProjectItem(item.name)) {
                razdel = 1;
            }
            if (this.checkLSRItem(item.name)) {
                razdel = 2;
            }
            if (this.checkLOGItem(item.name)) {
                razdel = 2;
            }
            
            items.push({
                name: item.name,
                razdel: razdel.toString(),
                count: item.count,
                price: item.price
            });
        }
        
        const today = new Date();
        const docDate = `${today.getDate().toString().padStart(2, '0')}.${(today.getMonth() + 1).toString().padStart(2, '0')}.${today.getFullYear()}`;
        
        return JSON.stringify({
            items: items,
            doc_date: docDate
        });
    }

    public async sendData(jsonData: string): Promise<void> {
        const soapEnvelope = `
<Envelope xmlns="http://schemas.xmlsoap.org/soap/envelope/" >
    <Body>
        <CreateCapitalConstruction xmlns="http://10.100.0.98/bpsps/">
            <JSONQuery>
                ${jsonData}
            </JSONQuery>
        </CreateCapitalConstruction>
    </Body>
</Envelope>
        `;
        logToUI("Данные для отправки");
        logToUI(soapEnvelope);
        try {
            const response = await fetch("http://10.100.0.98/bpsps/ws/ExcelImport.1cws", {
                method: "POST",
                headers: {
                    "Content-Type": "text/xml",
                    "Authorization": "Basic " + btoa("Администратор:qwerty123")
                },
                body: soapEnvelope
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            alert("Отправлено!");
        } catch (error) {
            console.error("Error sending data:", error);
            throw error;
        }
    }
}